/**
 * UserSearchCombobox - Search and select users from the school
 *
 * Generalized from StaffSearchCombobox to support filtering by user type.
 * Props:
 *   onSelect(user)       - Called when a user is picked
 *   excludeUserIds       - Array of UIDs to hide from results
 *   userTypes            - Array of types to include: 'staff', 'student', 'parent' (default: all)
 *   placeholder          - Input placeholder text
 *   disabled             - Disable the control
 */

import React, { useState, useEffect, useRef } from 'react';
import { UserService } from '@/services';
import { useSchool } from '@/contexts/SchoolContext';
import { Avatar } from '@/components/ui';
import {
  MagnifyingGlassIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const UserSearchCombobox = ({
  onSelect,
  excludeUserIds = [],
  userTypes,
  placeholder = 'Search for a user...',
  disabled = false,
}) => {
  const { schoolId } = useSchool();
  const [query, setQuery] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      if (!schoolId) return;
      setLoading(true);
      try {
        const users = await UserService.getSchoolUsers(schoolId);
        let filtered = users.filter(u => !excludeUserIds.includes(u.id));

        // Filter by userTypes if specified
        if (userTypes && userTypes.length > 0) {
          filtered = filtered.filter(u => {
            if (userTypes.includes('staff') && (u.permissions?.admin || u.permissions?.staff || u.role === 'teacher' || u.admin || u.staff)) return true;
            if (userTypes.includes('parent') && (u.permissions?.parent || u.parent)) return true;
            if (userTypes.includes('student')) return false; // Students are in students collection, not users
            return false;
          });
        }

        setAllUsers(filtered);
      } catch (error) {
        console.error('[UserSearchCombobox] Error fetching users:', error);
        setAllUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [schoolId, JSON.stringify(excludeUserIds), JSON.stringify(userTypes)]);

  // Filter based on query (fuzzy search)
  useEffect(() => {
    if (!query) {
      setFilteredUsers([]);
      setIsOpen(false);
      return;
    }

    const searchTerms = query.toLowerCase().split(' ').filter(t => t.length > 0);

    const results = allUsers.filter(user => {
      const searchString = `${user.displayName || ''} ${user.email || ''}`.toLowerCase();
      return searchTerms.every(term => searchString.includes(term));
    });

    // Sort: name matches first
    results.sort((a, b) => {
      const aMatch = (a.displayName || '').toLowerCase().includes(query.toLowerCase());
      const bMatch = (b.displayName || '').toLowerCase().includes(query.toLowerCase());
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return 0;
    });

    setFilteredUsers(results.slice(0, 10));
    setIsOpen(results.length > 0);
    setSelectedIndex(0);
  }, [query, allUsers]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredUsers.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < filteredUsers.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredUsers[selectedIndex]) handleSelect(filteredUsers[selectedIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        setQuery('');
        break;
    }
  };

  const handleSelect = (user) => {
    onSelect(user);
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(0);
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        inputRef.current && !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled || loading}
          className="input input-bordered input-sm w-full pl-9"
        />
        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <span className="loading loading-spinner loading-xs"></span>
          </div>
        )}
      </div>

      {isOpen && filteredUsers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden"
        >
          <div className="max-h-64 overflow-y-auto">
            {filteredUsers.map((user, index) => (
              <div
                key={user.id}
                onClick={() => handleSelect(user)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                  index === selectedIndex ? 'bg-primary/10' : 'hover:bg-base-200'
                }`}
              >
                <Avatar
                  src={user.photoURL}
                  name={user.displayName || user.email}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {user.displayName || 'Unnamed User'}
                  </div>
                  <div className="text-xs text-base-content/60 truncate">
                    {user.email}
                  </div>
                </div>
                {index === selectedIndex && (
                  <CheckIcon className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSearchCombobox;
